import React from "react";
import styled, { keyframes } from "styled-components";
import FlipFadeText from "./FlipFadeText";

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(
      circle at top,
      rgba(201, 146, 42, 0.08),
      transparent 30%
    ),
    rgba(10, 12, 16, 0.94);
  backdrop-filter: blur(8px);
  z-index: ${({ theme }) => theme.zIndex.modal};
  padding: 1.5rem;
`;

const LoaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const SpinnerShell = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

const SpinnerRing = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.08);
  border-top-color: ${({ theme }) => theme.colors.gold};
  border-right-color: rgba(224, 168, 58, 0.4);
  animation: ${rotate} 0.9s linear infinite;
`;

const SpinnerCore = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(201, 146, 42, 0.26),
    rgba(201, 146, 42, 0.05) 55%,
    transparent 72%
  );
  box-shadow: 0 0 24px rgba(201, 146, 42, 0.18);
`;

const Label = styled.div`
  margin-top: -0.5rem;
  margin-bottom: 0.2rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: 0.28em;
  text-transform: uppercase;
`;

const Hint = styled.p`
  max-width: 320px;
  margin: -0.4rem 0 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

const InlineWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  padding: ${({ theme }) => theme.spacing.xl};
`;

const InlineLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
`;

const InlineRing = styled.div`
  width: ${({ $size }) => $size || "38px"};
  height: ${({ $size }) => $size || "38px"};
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.gold};
  border-right-color: rgba(224, 168, 58, 0.45);
  animation: ${rotate} 0.85s linear infinite;
`;

const InlineLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.18em;
`;

const ButtonRing = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(26, 16, 0, 0.3);
  border-top-color: #1a1000;
  animation: ${rotate} 0.7s linear infinite;
`;

export const PageSpinner = () => (
  <Overlay role="status" aria-label="Loading">
    <LoaderContent>
      <SpinnerShell aria-hidden="true">
        <SpinnerRing />
        <SpinnerCore />
      </SpinnerShell>
      <Label>MetroCrafts Interiors</Label>
      <FlipFadeText textColor="#f0ede8" minHeight="120px" />
      <Hint>Loading your experience with a polished finish.</Hint>
    </LoaderContent>
  </Overlay>
);

export const InlineSpinner = ({ size, label = "Loading" }) => (
  <InlineWrapper role="status" aria-label={label}>
    <InlineLoader>
      <InlineRing $size={size} aria-hidden="true" />
      <InlineLabel>{label}</InlineLabel>
    </InlineLoader>
  </InlineWrapper>
);

export const ButtonSpinner = () => <ButtonRing aria-hidden="true" />;

export default PageSpinner;
